import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  featureRequestScreenshotUrls,
  listFeatureRequests,
  mergeFeatureRequest,
  saveFeatureIdea,
  setFeatureRequestBlock,
  updateFeatureRequest,
  type AdminFeatureRequest,
  type FeatureRequestStatus,
} from './api';
import { onlyDate } from './money';
import { RELEASE_NOTE_OPTIONS } from './releaseNoteOptions';

/**
 * Feature requests: what people asked for from Account > Help & support > Request a
 * feature (or through the assistant). Set a status and reply here and the sender
 * gets a notification; merge a duplicate and its sender follows the original. Ideas
 * on the public board are written separately, in your own words, under Ideas.
 */

type SettableStatus = Exclude<FeatureRequestStatus, 'merged'>;

export const STATUS_LABEL: Record<FeatureRequestStatus, string> = {
  sent: 'New',
  under_review: 'Under review',
  planned: 'Planned',
  in_progress: 'Being built',
  shipped: 'In the app',
  not_planned: 'Not planned',
  merged: 'Merged',
};

const SETTABLE: SettableStatus[] = ['sent', 'under_review', 'planned', 'in_progress', 'shipped', 'not_planned'];
const OPEN = new Set<FeatureRequestStatus>(['sent', 'under_review', 'planned', 'in_progress']);

const CATEGORY_LABEL: Record<string, string> = {
  assets: 'Assets and places',
  inspections: 'Inspections',
  work: 'Jobs and work',
  schedules: 'Schedules',
  reports: 'Reports',
  team: 'Team and contractors',
  integrations: 'Integrations and API',
  other: 'Other',
};

const IMPORTANCE_LABEL: Record<AdminFeatureRequest['importance'], string> = {
  nice_to_have: 'Nice to have',
  saves_time: 'Saves time',
  blocking: 'Blocking',
};

type Filter = 'open' | 'all' | FeatureRequestStatus;

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(rows: AdminFeatureRequest[]) {
  const header = ['sent', 'title', 'description', 'category', 'importance', 'status', 'votes', 'workspaces', 'from', 'email', 'workspace', 'plan', 'reply', 'source', 'app_version', 'platform'];
  const lines = rows.map((r) =>
    [
      r.created_at, r.title, r.description, r.category, r.importance, r.status, r.vote_count, r.workspace_count,
      r.requester_name, r.requester_email, r.workspace_name, r.plan_key, r.admin_reply, r.source,
      r.context?.appVersion, r.context?.platform,
    ].map(csvCell).join(','),
  );
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `feature-requests-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function FeatureRequests({ toast }: { toast: (message: string) => void }) {
  const [rows, setRows] = useState<AdminFeatureRequest[] | null>(null);
  const [filter, setFilter] = useState<Filter>('open');
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await listFeatureRequests());
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
      setRows([]);
    }
  }, [toast]);
  useEffect(() => void load(), [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (filter === 'open' ? !OPEN.has(r.status) : filter !== 'all' && r.status !== filter) return false;
      if (category && r.category !== category) return false;
      if (q && !`${r.title} ${r.description} ${r.requester_email ?? ''} ${r.workspace_name ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filter, category, query]);

  const selected = rows?.find((r) => r.id === selectedId) ?? null;

  if (rows === null) return <p className="muted">Loading requests…</p>;

  return (
    <>
      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {(['open', 'all', ...SETTABLE, 'merged'] as Filter[]).map((f) => (
          <button key={f} className={`chip ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
            {f === 'open' ? 'Open' : f === 'all' ? 'All' : STATUS_LABEL[f]}
          </button>
        ))}
      </div>
      <div className="row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <select className="field" style={{ maxWidth: 220 }} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
          <option value="">Every part of the app</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input style={{ maxWidth: 320 }} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title, text, email, workspace" />
        <button className="btn" onClick={() => downloadCsv(visible)} disabled={!visible.length}>Export CSV</button>
      </div>

      {selected ? (
        <RequestDetail
          key={selected.id}
          request={selected}
          all={rows}
          toast={toast}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      ) : null}

      {visible.length === 0 ? (
        <p className="muted">{rows.length === 0 ? 'Nobody has requested a feature yet.' : 'No requests match.'}</p>
      ) : (
        <div className="matrix-scroll">
          <table className="matrix">
            <thead>
              <tr><th>Sent</th><th>Request</th><th>Area</th><th>Matters</th><th>Status</th><th>Votes</th><th>From</th></tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  style={{ cursor: 'pointer', background: r.id === selectedId ? 'var(--panel2)' : undefined, opacity: r.status === 'merged' ? 0.6 : 1 }}
                >
                  <td>{onlyDate(r.created_at)}</td>
                  <td style={{ whiteSpace: 'normal', maxWidth: 420 }}>
                    <strong>{r.title}</strong>
                    {r.source === 'assistant' ? <span className="pill" style={{ marginLeft: 6 }}>Assistant</span> : null}
                    {r.screenshot_paths.length ? <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>{r.screenshot_paths.length} screenshot(s)</span> : null}
                  </td>
                  <td>{CATEGORY_LABEL[r.category] ?? r.category}</td>
                  <td>{IMPORTANCE_LABEL[r.importance] ?? r.importance}</td>
                  <td>{STATUS_LABEL[r.status] ?? r.status}</td>
                  <td title="Votes, and how many workspaces they come from">{r.vote_count} · {r.workspace_count} ws</td>
                  <td>
                    {r.requester_email ?? <span className="muted">Deleted account</span>}
                    {r.workspace_name ? <div className="muted" style={{ fontSize: 12 }}>{r.workspace_name}{r.plan_key ? ` · ${r.plan_key}` : ''}</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function RequestDetail({
  request,
  all,
  toast,
  onClose,
  onChanged,
}: {
  request: AdminFeatureRequest;
  all: AdminFeatureRequest[];
  toast: (message: string) => void;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [status, setStatus] = useState<SettableStatus>(request.status === 'merged' ? 'under_review' : request.status);
  const [reply, setReply] = useState(request.admin_reply ?? '');
  const [release, setRelease] = useState(request.shipped_release ?? '');
  const [mergeInto, setMergeInto] = useState('');
  const [shots, setShots] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void featureRequestScreenshotUrls(request.screenshot_paths).then(setShots).catch(() => setShots([]));
  }, [request.screenshot_paths]);

  const run = async (work: () => Promise<void>, done: string) => {
    setBusy(true);
    try {
      await work();
      toast(done);
      await onChanged();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const mergeTargets = all.filter((r) => r.id !== request.id && r.status !== 'merged');
  const context = Object.entries(request.context ?? {}).filter(([, v]) => v);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0 }}>{request.title}</h2>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            {onlyDate(request.created_at)} · {CATEGORY_LABEL[request.category] ?? request.category} · {IMPORTANCE_LABEL[request.importance]}
            {' · '}{request.requester_name ?? request.requester_email ?? 'Deleted account'}
            {request.workspace_name ? ` · ${request.workspace_name}` : ''}
            {request.requester_blocked ? ' · Blocked from sending' : ''}
          </p>
        </div>
        <button className="btn" onClick={onClose}>Close</button>
      </div>

      <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{request.description}</p>

      {shots.length ? (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {shots.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer">
              <img src={url} alt="Screenshot" style={{ height: 140, borderRadius: 8, border: '1px solid var(--border)' }} />
            </a>
          ))}
        </div>
      ) : null}

      {context.length ? (
        <p className="muted" style={{ fontSize: 12 }}>
          {context.map(([k, v]) => `${k}: ${v}`).join(' · ')}
        </p>
      ) : null}

      {request.status === 'merged' ? (
        <p className="muted">Merged into another request. Saving a status here takes it back out.</p>
      ) : null}

      <label>Status</label>
      <select className="field" value={status} onChange={(e) => setStatus(e.target.value as SettableStatus)} style={{ maxWidth: 260 }}>
        {SETTABLE.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
      </select>
      {status === 'shipped' ? (
        <>
          <label>Shipped in (What's new note the sender can open)</label>
          <select className="field" value={release} onChange={(e) => setRelease(e.target.value)} style={{ maxWidth: 420 }}>
            <option value="">No note</option>
            {RELEASE_NOTE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </>
      ) : null}
      <label>Reply to the sender (they see it under Your requests)</label>
      <textarea className="field" value={reply} maxLength={2000} onChange={(e) => setReply(e.target.value)} placeholder="Optional" />
      <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn primary"
          disabled={busy}
          onClick={() => void run(() => updateFeatureRequest(request.id, status, reply.trim() || null, status === 'shipped' ? release || null : null), 'Saved. The sender has been told.')}
        >
          Save and notify
        </button>
        <button
          className="btn"
          disabled={busy}
          title="Creates an unpublished idea in your words; edit and publish it under Ideas"
          onClick={() =>
            void run(async () => {
              await saveFeatureIdea({
                ideaId: request.idea_id,
                title: request.title,
                summary: '',
                status: 'considering',
                published: false,
                shippedRelease: null,
                fromRequestId: request.id,
              });
            }, 'Draft idea saved under Ideas. Reword it there before you publish.')
          }
        >
          {request.idea_id ? 'Link to its idea again' : 'Start an idea from this'}
        </button>
      </div>

      <label>Merge into another request (this sender's vote moves there)</label>
      <div className="row" style={{ gap: 8 }}>
        <select className="field" value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} style={{ maxWidth: 420 }}>
          <option value="">Choose a request</option>
          {mergeTargets.map((r) => <option key={r.id} value={r.id}>{onlyDate(r.created_at)} · {r.title}</option>)}
        </select>
        <button
          className="btn"
          disabled={busy || !mergeInto}
          onClick={() => void run(() => mergeFeatureRequest(request.id, mergeInto), 'Merged. The sender has been told.')}
        >
          Merge
        </button>
      </div>

      {request.user_id ? (
        <div className="row" style={{ marginTop: 16 }}>
          <button
            className="btn"
            disabled={busy}
            onClick={() => {
              const blocking = !request.requester_blocked;
              const reason = blocking ? window.prompt('Why? (kept for your records)') : null;
              if (blocking && reason === null) return;
              void run(
                () => setFeatureRequestBlock(request.user_id as string, blocking, reason),
                blocking ? 'This person can no longer send requests.' : 'This person can send requests again.',
              );
            }}
          >
            {request.requester_blocked ? 'Let this person send requests again' : 'Stop this person sending requests'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
